// src/components/dashboard/RecentCallsList.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow, parseISO } from 'date-fns'; // Import date-fns functions
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge'; // Import Badge
// Assuming cn utility might be useful later, keep import commented if unused for now
// import { cn } from "@/lib/utils";

interface RecentCallData {
  id: string;
  time: string; // ISO string
  number: string | null;
  outcome: string | null; // Keep as string, matching function input
}

interface RecentCallsListProps {
  data: RecentCallData[] | undefined;
  isLoading: boolean;
}

// Helper to get outcome badge variant based on UI/UX best practices
const getOutcomeBadgeVariant = (outcome: string | null): "success" | "destructive" | "secondary" | "outline" => {
  // Normalize the outcome string for reliable comparison (lowercase, trim whitespace)
  const normalizedOutcome = outcome?.toLowerCase().trim() ?? 'unknown';

  // Map outcomes to variants
  switch (normalizedOutcome) {
    // Positive / Success (Green)
    case 'appointment scheduled':
    case 'appointment rescheduled':
    case 'appointment confirmed':
      return 'success';

    // Negative / Needs Attention (Red)
    case 'appointment cancelled':
    case 'call failed':
    case 'hung up': // Customer ended call abruptly
      return 'destructive';

    // Neutral / Informational / Incomplete (Gray)
    case 'information provided':
    case 'transferred':
    case 'left voicemail':
    case 'no answer':
      return 'secondary';

    // Ambiguous / Other (Subtle Border)
    case 'other':
    case 'unknown': // Handle null or undefined outcomes explicitly
    default:
      return 'outline'; // Use outline for least emphasis
  }
};

// Helper to format time nicely (Keep existing)
const formatCallTime = (isoTime: string): string => {
    try {
        // Format as relative time (e.g., "about 5 hours ago")
        return formatDistanceToNow(parseISO(isoTime), { addSuffix: true });
    } catch {
        // Fallback for invalid date strings
        return "Invalid date";
    }
}

export default function RecentCallsList({ data, isLoading }: RecentCallsListProps) {
  const hasData = data && data.length > 0;

  return (
    <Card className="h-full"> {/* Allow card to take full height if needed in grid */}
      <CardHeader>
        {/* Adjusted title for clarity based on typical usage */}
        <CardTitle className="text-base font-medium text-muted-foreground">Recent Calls</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          // Skeleton Loading State
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => ( // Show 4 skeleton items
              <div key={i} className="flex items-center space-x-3">
                {/* Consider adding a phone icon skeleton */}
                <Skeleton className="h-5 w-5 rounded-full" />
                <div className="space-y-1 flex-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                 {/* Outcome skeleton */}
                 <Skeleton className="h-5 w-24 rounded-md" />
              </div>
            ))}
          </div>
        ) : hasData ? (
          // Data Loaded State
          <ul className="space-y-4">
            {data.map((call) => (
               <li key={call.id} className="flex items-center space-x-3">
                   {/* TODO: Consider adding an icon based on outcome if desired */}
                   {/* Example: <Phone className="h-4 w-4 text-muted-foreground" /> */}
                   <div className="flex-1 overflow-hidden">
                       <p className="text-sm font-medium text-foreground truncate">
                           {/* Link to specific call log page if available, otherwise keep generic */}
                           <Link to="/dashboard/calls" className="hover:underline" title={`Call to ${call.number || 'Unknown'}`}>
                               Call to {call.number || 'Unknown'}
                           </Link>
                       </p>
                       <p className="text-xs text-muted-foreground" title={new Date(call.time).toLocaleString()}>
                           {formatCallTime(call.time)}
                       </p>
                   </div>
                   {/* Apply the dynamically determined badge variant */}
                   <Badge variant={getOutcomeBadgeVariant(call.outcome)} className="whitespace-nowrap text-xs px-2 py-0.5">
                       {/* Display the original outcome string or 'Unknown' */}
                       {call.outcome || 'Unknown'}
                   </Badge>
               </li>
            ))}
            {/* Link to view all calls */}
             <li className="pt-2 border-t border-border">
                 <Link to="/dashboard/calls" className="text-sm text-primary hover:underline font-medium">
                     View all calls &rarr;
                 </Link>
             </li>
          </ul>
        ) : (
          // Empty State
          <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
             {/* Updated empty state message */}
             No recent calls found for this period.
          </div>
        )}
      </CardContent>
    </Card>
  );
}